import { format, formatInTimeZone } from 'date-fns-tz'
import { formatDistanceToNow } from 'date-fns'
import { enUS, ru, ko, ja, zhCN, zhTW } from 'date-fns/locale'

// Map language codes to date-fns locales
const localeMap: Record<string, Locale> = {
  en: enUS,
  'en-US': enUS,
  ru: ru,
  'ru-RU': ru,
  ko: ko,
  'ko-KR': ko,
  ja: ja,
  'ja-JP': ja,
  zh: zhCN,
  'zh-CN': zhCN,
  tw: zhTW,
  'zh-TW': zhTW
}

/**
 * Format a date using date-fns with proper localization
 * @param date The date to format
 * @param formatStr The format string to use
 * @param language The language code (e.g., 'en', 'ru')
 * @returns Formatted date string
 */
export function formatDate(date: Date | number | string, formatStr: string, language: string = 'en'): string {
  if (!date) return ''
  
  // Try to find the exact locale or use the base language
  const locale = localeMap[language] || localeMap[language.split('-')[0]] || enUS
  
  // Convert format patterns to match what other UI components are using
  // Replace "do" with "d" to avoid ordinals which may not translate properly
  const safeFormat = formatStr.replace(/do\b/g, 'd')
  
  let formatted = format(new Date(date), safeFormat, { locale })
  
  // Ensure month and day names are capitalized for languages like Russian
  const firstChar = formatted.charAt(0)
  formatted = firstChar.toUpperCase() + formatted.slice(1)
  
  return formatted
}

/**
 * Format relative distance from now (e.g., "2 days ago")
 * @param date The date to format
 * @param options Options for formatting
 * @param language The language code (e.g., 'en', 'ru')
 * @returns Formatted relative time string
 */
export function formatRelativeTime(
  date: Date | number | string,
  options: { addSuffix?: boolean } = { addSuffix: true },
  language: string = 'en'
): string {
  if (!date) return ''
  
  // Try to find the exact locale or use the base language
  const locale = localeMap[language] || localeMap[language.split('-')[0]] || enUS
  
  const formatted = formatDistanceToNow(new Date(date), {
    addSuffix: options.addSuffix,
    locale
  })
  
  // Capitalize the first letter for consistency across languages
  const firstChar = formatted.charAt(0)
  return firstChar.toUpperCase() + formatted.slice(1)
}

/**
 * Format a date in a specific timezone using date-fns-tz
 * @param date The date to format
 * @param timezone The timezone to format in (e.g., 'Europe/Moscow')
 * @param formatStr The format string to use
 * @param language The language code (e.g., 'en', 'ru')
 * @returns Formatted date string
 */
export function formatDateInTimezone(
  date: Date | number | string,
  timezone: string,
  formatStr: string,
  language: string = 'en'
): string {
  if (!date) return ''
  
  // Try to find the exact locale or use the base language
  const locale = localeMap[language] || localeMap[language.split('-')[0]] || enUS
  
  // Convert format patterns to match what other UI components are using
  // Replace "do" with "d" to avoid ordinals which may not translate properly
  const safeFormat = formatStr.replace(/do\b/g, 'd')
  
  let formatted = formatInTimeZone(new Date(date), timezone, safeFormat, { locale })
  
  // Ensure month and day names are capitalized for languages like Russian
  const firstChar = formatted.charAt(0)
  formatted = firstChar.toUpperCase() + formatted.slice(1)
  
  return formatted
}